import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface CollageSearchParams {
  page?: number;
  images?: number;
  sortby?: string;
  sortdir?: string;
  pricelt?: number;
  pricegt?: number;
  product_type?: string;
  collage_type_slug?: string;
  no_of_frames?: string;
  frame_style?: string;
  color?: string;
  discount?: string;
  searchtext?: string;
}

@Injectable({ providedIn: 'root' })
export class CollageSetService {
  private base = environment.apiUrl;
  // New Laravel backend — the customer image upload has been migrated here.
  private newBase = environment.newApiUrl;
  private searchBase = environment.searchApiBase;

  constructor(private http: HttpClient) {}

  // Solr search for collage sets
  searchCollageSets(filters: CollageSearchParams = {}): Observable<any> {
    let params = new HttpParams()
      .set('page', String(filters.page || 1))
      .set('images', String(filters.images || 24))
      .set('sortby', filters.sortby || 'created_at')
      .set('sortdir', filters.sortdir || 'desc');

    if (filters.pricelt) params = params.set('pricelt', String(filters.pricelt));
    if (filters.pricegt) params = params.set('pricegt', String(filters.pricegt));
    if (filters.product_type) params = params.set('product_type', filters.product_type);
    if (filters.collage_type_slug) params = params.set('collage_type_slug', filters.collage_type_slug);
    if (filters.no_of_frames) params = params.set('no_of_frames', filters.no_of_frames);
    if (filters.frame_style) params = params.set('frame_style', filters.frame_style);
    if (filters.color) params = params.set('color', filters.color);
    if (filters.discount) params = params.set('discount', filters.discount);
    if (filters.searchtext) params = params.set('searchtext', filters.searchtext);

    // Use newSearchApiBase for collage search (api.indiapicture.in)
    const collageSearchBase = (environment as any).newSearchApiBase || this.searchBase;
    return this.http.get<any>(`${collageSearchBase}/search_collage_set.php`, { params });
  }

  // Collage types
  getCollageTypes(): Observable<any> {
    const params = new HttpParams()
      .set('filter', JSON.stringify([['status', '=', 1]]))
      .set('orderBy', '[]').set('pageNumber', '0').set('pageSize', '-1');
    return this.http.get<any>(`${this.newBase}/api/site/artset/collage-types`, { params });
  }

  // Collage frames (number of frames)
  getCollageFrames(): Observable<any> {
    const params = new HttpParams()
      .set('filter', '[]').set('orderBy', '[]')
      .set('pageNumber', '0').set('pageSize', '-1');
    return this.http.get<any>(`${this.newBase}/api/site/artset/collage-frames`, { params });
  }

  // Init artset customization (PDP data — single API for everything)
  initArtsetCustomization(slug: string, userId?: number): Observable<any> {
    let params = new HttpParams().set('collage_layout_caption', slug);
    if (userId) params = params.set('user_id', String(userId));
    return this.http.get<any>(`${this.newBase}/api/site/artset/init`, { params });
  }

  // Upload media (customer photo → GCS). Migrated to the new backend; same payload
  // ({ filename, image, created_by }) and same { url } response, so callers are
  // unchanged. Replaces the old backend's POST /api/unauth/uploadMedia.
  uploadMedia(filename: string, image: string, createdBy: number = 0): Observable<any> {
    return this.http.post<any>(`${this.newBase}/api/site/uploads/image`, { filename, image, created_by: createdBy });
  }

  // Get price for a frame
  getPriceDetail(frame: any, productType: number): Observable<any> {
    const params = new HttpParams()
      .set('frame', JSON.stringify(frame))
      .set('productType', String(productType))
      .set('product', '2')
      .set('checkRestriction', '0')
      .set('restrictionType', '0')
      .set('isFrontend', '1');
    return this.http.get<any>(`${this.base}/api/unauth/getPriceDetailArtCollage`, { params });
  }

  // Get total price for art set / collage
  getArtSetTotal(frameDetail: any[], productType: number, collageLayoutId: number, layoutId = 0): Observable<any> {
    return this.http.post<any>(`${this.newBase}/api/site/artset/total`, {
      frame: JSON.stringify(frameDetail),
      product_type: productType,
      collage_layout: collageLayoutId,
      layout_id: layoutId
    });
  }

  // Frame styles
  getFrameStyles(): Observable<any> {
    const params = new HttpParams()
      .set('filter', '[]').set('orderBy', '[]')
      .set('pageNumber', '0').set('pageSize', '-1');
    return this.http.get<any>(`${this.newBase}/api/site/frames/masters/frame_style`, { params });
  }
}
